import { useState } from "react";
import type { CoachFeedback, WritingRuleKey } from "@storyai/shared";

const RULE_LABEL: Record<WritingRuleKey, string> = {
  capitalization: "Capital letters",
  punctuation: "Punctuation",
  completeSentences: "Complete sentences",
  person: "Point of view",
  minWords: "Length",
  noTextSpeak: "Text-speak",
  descriptive: "Description",
  spelling: "Spelling",
};

/** The writing coach's note on one player message. Visually distinct from the story. */
export function CoachBubble({ feedback }: { feedback: CoachFeedback }) {
  const [showFixed, setShowFixed] = useState(false);
  const hasIssues = feedback.issues.length > 0;
  const words = feedback.wordsUsed ?? [];
  const earned = feedback.points.reading + feedback.points.vocabulary + feedback.points.writing;

  return (
    <div className={`coach${feedback.ok ? "" : " coach-blocked"}`} role="note" aria-label="Writing coach">
      <div className="coach-head">
        <span className="coach-icon" aria-hidden="true">
          ✎
        </span>
        <span className="coach-label">Coach</span>
        {earned > 0 && <span className="coach-points">+{earned}</span>}
      </div>
      {feedback.praise && <p className="coach-praise">{feedback.praise}</p>}
      {hasIssues && (
        <ul className="coach-issues">
          {feedback.issues.map((iss, i) => (
            <li key={i}>
              <span className="coach-rule">{RULE_LABEL[iss.rule] ?? iss.rule}</span>
              {iss.quote && <q className="coach-quote">{iss.quote}</q>}
              <span className="coach-arrow" aria-hidden="true">
                {" "}
                →{" "}
              </span>
              <span>{iss.suggestion}</span>
            </li>
          ))}
        </ul>
      )}
      {words.length > 0 && (
        <ul className="coach-words">
          {words.map((w, i) => (
            <li key={i} className={w.correct ? "ok" : "retry"}>
              <strong>{w.word}</strong> {w.correct ? "used well" : "try again"}
              {w.note && <span className="muted small"> — {w.note}</span>}
            </li>
          ))}
        </ul>
      )}
      {feedback.corrected && hasIssues && (
        <div className="coach-fixed">
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowFixed((v) => !v)}>
            {showFixed ? "Hide the fixed version" : "See it fixed"}
          </button>
          {showFixed && <blockquote className="coach-fixed-text">{feedback.corrected}</blockquote>}
        </div>
      )}
      {!feedback.ok && <p className="coach-blocked-note">Fix the required items and send again.</p>}
    </div>
  );
}
